import React, { useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Group,
  ScrollArea,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { formatTime } from "@util/dates";
import { useChat } from "./ChatContext";
import { conversationTitle, liveTypingFor } from "./chatHelpers";
import classes from "./ChatTray.module.css";

export default function ChatConversationList() {
  const { t } = useTranslation();
  const {
    me,
    conversations,
    activeId,
    openConversation,
    setNewOpen,
    conversationIsOnline,
    loadingInbox,
    typingMap,
  } = useChat();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((row) =>
      conversationTitle(row, me, t).toLowerCase().includes(q)
    );
  }, [conversations, me, query, t]);

  return (
    <div className={classes.listPane}>
      <Box p="sm">
        <Group justify="space-between" mb="xs">
          <Text fw={600}>{t("chat")}</Text>
          <ActionIcon
            variant="light"
            onClick={() => setNewOpen(true)}
            aria-label={t("chat_new")}
          >
            <IconPlus size={16} />
          </ActionIcon>
        </Group>
        <TextInput
          size="xs"
          placeholder={t("chat_search")}
          leftSection={<IconSearch size={14} />}
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
        />
      </Box>
      <ScrollArea style={{ flex: 1 }}>
        <Box p="xs">
          {!loadingInbox && filtered.length === 0 && (
            <Text size="sm" c="dimmed" p="xs">
              {t("chat_empty")}
            </Text>
          )}
          {filtered.map((conversation) => {
            const active = String(conversation.id) === String(activeId);
            const online = conversationIsOnline(conversation);
            const preview = liveTypingFor(typingMap, conversation.id)
              ? t("chat_typing")
              : conversation.last_message?.body || "";
            return (
              <UnstyledButton
                key={conversation.id}
                className={`${classes.row} ${active ? classes.rowActive : ""}`}
                p="xs"
                mb={4}
                w="100%"
                onClick={() => openConversation(conversation.id)}
              >
                <Group wrap="nowrap" gap="xs" align="flex-start">
                  <span
                    className={`${classes.dot} ${online ? classes.dotOnline : ""}`}
                  />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group justify="space-between" wrap="nowrap" gap={4}>
                      <Text size="sm" fw={600} truncate>
                        {conversationTitle(conversation, me, t)}
                      </Text>
                      {conversation.last_message_at && (
                        <Text size="xs" c="dimmed">
                          {formatTime(conversation.last_message_at)}
                        </Text>
                      )}
                    </Group>
                    <Group justify="space-between" wrap="nowrap" gap={4}>
                      <Text size="xs" c="dimmed" truncate>
                        {preview}
                      </Text>
                      {conversation.unread_count > 0 && (
                        <Badge size="xs" circle>
                          {conversation.unread_count}
                        </Badge>
                      )}
                    </Group>
                  </Box>
                </Group>
              </UnstyledButton>
            );
          })}
        </Box>
      </ScrollArea>
    </div>
  );
}
